import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Shield, DollarSign, Users, Building2, MessageCircle, FileText } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: React.ReactNode;
  category: string;
  icon: React.ReactNode;
}

const FAQ = () => {
  const [openItem, setOpenItem] = useState<number | null>(null);

  const faqs: FAQItem[] = [
    // General Questions
    {
      category: "General",
      icon: <Building2 className="h-5 w-5 text-indigo-600" />,
      question: "What is SubleaseHero?",
      answer: "SubleaseHero is a secure platform that connects people who need to sublease their apartments with verified subletters. We provide a discreet way to list your property and find reliable tenants, with built-in privacy features and verification processes."
    },
    {
      category: "General",
      icon: <Building2 className="h-5 w-5 text-indigo-600" />,
      question: "Which cities do you operate in?",
      answer: "We currently operate in New York City (Manhattan and Brooklyn) and Miami (Miami Beach and Downtown Miami) areas. We're continuously expanding to new locations."
    },

    // For Property Owners
    {
      category: "For Property Owners",
      icon: <Shield className="h-5 w-5 text-indigo-600" />,
      question: "Is it free to list my property?",
      answer: "Yes, listing your property is completely free. You only pay a 5% service fee (based on one month's rent) when you find a qualified subletter and they pay their good faith deposit."
    },
    {
      category: "For Property Owners",
      icon: <Shield className="h-5 w-5 text-indigo-600" />,
      question: "How are subletters verified?",
      answer: "All potential subletters go through our verification process, which includes ID verification, employment verification (if required), and background checks. You can also set specific requirements for income, credit score, and other criteria."
    },
    {
      category: "For Property Owners",
      icon: <Shield className="h-5 w-5 text-indigo-600" />,
      question: "How is my privacy protected?",
      answer: "Your listing's exact address and contact information remain private until a subletter is approved and pays their deposit. Photos are automatically screened for identifying details, and all communication happens through our secure messaging system."
    },

    // For Subletters
    {
      category: "For Subletters",
      icon: <Users className="h-5 w-5 text-indigo-600" />,
      question: "Do I need a premium membership to view listings?",
      answer: "Yes, a premium membership is required to view full listing details and contact property owners. This ensures serious inquiries and maintains platform quality."
    },
    {
      category: "For Subletters",
      icon: <Users className="h-5 w-5 text-indigo-600" />,
      question: "What is the good faith deposit?",
      answer: "When your application is approved, you'll need to pay a good faith deposit equal to 5% of one month's rent. This is a non-refundable service fee kept by SubleaseHero to maintain platform quality and ensure serious inquiries."
    },

    // Process & Payments
    {
      category: "Process & Payments",
      icon: <DollarSign className="h-5 w-5 text-indigo-600" />,
      question: "How does the sublease process work?",
      answer: (
        <ol className="list-decimal pl-4 space-y-2">
          <li>Submit an application for a property</li>
          <li>If approved, pay the 5% good faith deposit</li>
          <li>Contact information is shared between parties</li>
          <li>Coordinate security deposit and lease signing directly</li>
          <li>Complete move-in process</li>
        </ol>
      )
    },
    {
      category: "Process & Payments",
      icon: <DollarSign className="h-5 w-5 text-indigo-600" />,
      question: "How are rent payments handled?",
      answer: "After the sublease is finalized, rent payments are handled directly between the subletter and property owner. SubleaseHero only facilitates the initial good faith deposit payment."
    },

    // Security & Support
    {
      category: "Security & Support",
      icon: <MessageCircle className="h-5 w-5 text-indigo-600" />,
      question: "What happens after I pay the good faith deposit?",
      answer: "After paying the deposit, contact information is shared between both parties. You'll receive move-in instructions, house rules, and can coordinate the security deposit, lease signing, and key exchange directly with the other party."
    },
    {
      category: "Security & Support",
      icon: <FileText className="h-5 w-5 text-indigo-600" />,
      question: "Do you provide a sublease agreement?",
      answer: "While we recommend creating a formal sublease agreement, this is handled directly between the property owner and subletter. We focus on facilitating the connection and initial verification process."
    }
  ];

  const categories = Array.from(new Set(faqs.map(faq => faq.category)));

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
          <p className="text-lg text-gray-600">
            Everything you need to know about subletting with SubleaseHero
          </p>
        </div>

        <div className="space-y-8">
          {categories.map((category, categoryIndex) => (
            <div key={categoryIndex}>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{category}</h2>
              <div className="space-y-4">
                {faqs
                  .filter(faq => faq.category === category)
                  .map((faq, index) => {
                    const itemIndex = categoryIndex * 100 + index;
                    const isOpen = openItem === itemIndex;

                    return (
                      <div 
                        key={index}
                        className="bg-white rounded-lg shadow-sm overflow-hidden"
                      >
                        <button
                          onClick={() => setOpenItem(isOpen ? null : itemIndex)}
                          className="w-full text-left px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center pr-6">
                            <div className="mr-3 flex-shrink-0">
                              {faq.icon}
                            </div>
                            <h3 className="text-base font-medium text-gray-900">
                              {faq.question}
                            </h3>
                          </div>
                          {isOpen ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                        
                        {isOpen && (
                          <div className="px-6 pb-4">
                            <div className="prose prose-sm max-w-none text-gray-600">
                              {faq.answer}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>

        {/* Support Section */}
        <div className="mt-12 bg-indigo-50 rounded-xl p-6 text-center">
          <h2 className="text-lg font-semibold text-indigo-900 mb-2">
            Still have questions?
          </h2>
          <p className="text-indigo-700 mb-4">
            We're here to help! Contact our support team.
          </p>
          <a
            href="mailto:support@subleasehero.com"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default FAQ;